import React, { useEffect, useRef } from "react";
import {
	Navigate,
	Route,
	BrowserRouter as Router,
	Routes,
	useLocation,
} from "react-router-dom";
import "./assets/scss/app.css";
import { useAuth } from "./context/authContext";
import privateRoutes from "./routes/privateRoutes";
import publicRoutes from "./routes/publicRoutes";

const App: React.FC = () => {
	const { isAuthenticated, isLoading, checkAuthStatus } = useAuth();
	
	// Don't render routes until auth state is loaded from localStorage
	if (isLoading) {
		return (
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				height: '100vh',
				fontSize: '1.2rem',
				color: '#1D387D'
			}}>
				Loading...
			</div>
		);
	}
	
	return (
		<Router>
			<LocationListener checkAuthStatus={checkAuthStatus} />
			<Routes>
				{!isAuthenticated &&
					publicRoutes.map((route) => (
						<Route key={`public-${route.path}`} path={route.path} element={route.element} />
					))}
				<Route
					path="/*"
					element={
						isAuthenticated ? (
							<Routes>
								{privateRoutes.map((route) => (
									<Route
										key={`private-${route.path}`}
										path={route.path}
										element={route.element}
									/>
								))}
							</Routes>
						) : (
							<Navigate to="/login" replace />
						)
					}
				/>
			</Routes>
		</Router>
	);
};

const LocationListener: React.FC<{ checkAuthStatus: () => void }> = ({
	checkAuthStatus,
}) => {
	const location = useLocation();
	const lastAuthCheck = useRef<number>(0);
	const AUTH_CHECK_COOLDOWN = 5000; // 5 seconds cooldown between auth checks
	
	useEffect(() => {
		// Set data-route attribute for CSS targeting
		document.body.setAttribute('data-route', location.pathname);
		
		return () => {
			document.body.removeAttribute('data-route');
		};
	}, [location.pathname]);

	useEffect(() => {
		const now = Date.now();
		
		// Only check auth if enough time has passed since last check
		if (now - lastAuthCheck.current > AUTH_CHECK_COOLDOWN) {
			checkAuthStatus();
			lastAuthCheck.current = now;
		}
		
		// Clear maturity assessment selections when navigating to chat or login
		if (location.pathname !== '/maturityAssessment') {
			Object.keys(localStorage).forEach(key => {
				if (key.startsWith('maturity_selected')) {
					localStorage.removeItem(key);
				}
			});
			console.log('🧹 Cleared maturity selections for route:', location.pathname);
		}
	}, [location, checkAuthStatus]);

	return null;
};

export default App;